<template>
  <div class="agent-model-form-container">
    <div class="content-area">
      <div class="page-header">
        <div class="page-header-row">
          <div class="header-left d-flex align-center">
            <v-btn
              icon
              variant="text"
              class="back-btn mr-2"
              :to="{ name: 'AgentModels' }">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              {{ isEdit ? 'Edit Agent Model' : 'Create Agent Model' }}
            </h1>
          </div>
          <div
            v-if="isEdit && !loadError"
            class="header-right">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              class="call-btn mank-btn-icon mank-btn-icon-md"
              title="Delete Agent Model"
              :disabled="loadingDetail || deleting"
              @click="showDeleteDialog = true">
              <Icon name="trash" variant="solid" color="default" size="16"/>
            </v-btn>
          </div>
        </div>
      </div>

      <div
        class="main-content"
        :class="{ 'main-content--split-row': !loadError && !loadingDetail }">
        <div v-if="loadError" class="error-banner pa-4 mb-4">
          {{ loadError }}
        </div>

        <div v-else-if="loadingDetail" class="py-12 text-center">
          <v-progress-circular indeterminate color="primary" size="40"/>
        </div>

        <div v-else class="agent-model-split-inner">
          <div class="left-panel">
            <div class="form-group">
              <label class="input-label required">Name <span class="required-asterisk">*</span></label>
              <MankTextField
                v-model="form.name"
                dense
                hide-details
                placeholder="e.g. gpt-4o-mini"/>
            </div>
            <div class="form-group">
              <label class="input-label required">Source <span class="required-asterisk">*</span></label>
              <MankSelect
                v-model="form.source"
                :items="sourceSelectItems"
                placeholder="TTS"
                disabled
                :menu-props="{
                  maxHeight: 300,
                  offsetY: true,
                  nudgeBottom: 8,
                  contentClass: 'mank-select-menu',
                }"
                hide-details>
                <template v-slot:prepend-inner>
                  <Icon name="agent" variant="solid" color="muted"/>
                </template>
                <template v-slot:append>
                  <Icon name="expand-down" variant="solid" color="muted"/>
                </template>
              </MankSelect>
            </div>
            <div class="system-prompt-section description-field">
              <div class="input-label">
                Description
              </div>
              <div class="text-box">
                <MankTextarea
                  v-model="form.description"
                  placeholder="Shown in admin and may help operators pick an Agent Model."
                  rows="4"
                  class="system-prompt-textarea"/>
              </div>
            </div>

            <div class="input-label section-title--spaced">
              External path
            </div>
            <div class="info-line mb-4">
              <div class="info-icon">
                <Icon name="info-circle" variant="solid" size="16" color="info"/>
              </div>
              <p class="info-text mb-0">
                URL or path your backend uses for this Agent Model (custom LLM/TTS provider). Required for every Agent Model.
              </p>
            </div>
            <div class="form-group">
              <label class="input-label required">External path <span class="required-asterisk">*</span></label>
              <MankTextField
                v-model="form.external_path"
                dense
                hide-details
                placeholder="https://api.example.com/v1/tts"/>
            </div>
            <div class="form-group">
              <label class="input-label">Content type</label>
              <MankTextField
                v-model="form.content_type"
                dense
                hide-details
                placeholder="e.g. application/json (optional)"/>
            </div>
          </div>

          <div class="right-panel">
            <div class="input-label">
              Parameters
            </div>
            <p class="params-help text-body-2 mb-4">
              Names and types for runtime fields. For <strong>file</strong> type, use <strong>Max</strong> for the maximum upload size in MB (optional).
            </p>

            <div class="params-table-scroll">
              <table class="params-table" aria-label="Agent Model parameters">
                <colgroup>
                  <col class="colgroup-name">
                  <col class="colgroup-type">
                  <col class="colgroup-req">
                  <col class="colgroup-num">
                  <col class="colgroup-num">
                  <col class="colgroup-def">
                  <col class="colgroup-act">
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">
                      Name
                    </th>
                    <th scope="col">
                      Type
                    </th>
                    <th scope="col">
                      Required
                    </th>
                    <th scope="col">
                      Min
                    </th>
                    <th scope="col">
                      Max <span class="th-sub">(MB if file)</span>
                    </th>
                    <th scope="col">
                      Default
                    </th>
                    <th scope="col" class="th-action"/>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in form.params"
                    :key="idx">
                    <td data-label="Name">
                      <MankTextField
                        v-model="row.name"
                        dense
                        hide-details
                        placeholder="param name"
                        class="param-field"/>
                    </td>
                    <td data-label="Type">
                      <MankSelect
                        v-model="row.type"
                        :items="paramTypes"
                        item-text="text"
                        item-value="value"
                        class="param-field"
                        :menu-props="{
                          maxHeight: 300,
                          offsetY: true,
                          nudgeBottom: 8,
                          contentClass: 'mank-select-menu',
                        }"
                        hide-details
                        @change="onParamTypeChange(row)">
                        <template v-slot:prepend-inner>
                          <Icon name="text-box" variant="solid" color="muted"/>
                        </template>
                        <template v-slot:append>
                          <Icon name="expand-down" variant="solid" color="muted"/>
                        </template>
                      </MankSelect>
                    </td>
                    <td data-label="Required">
                      <v-checkbox
                        v-model="row.required"
                        dense
                        hide-details
                        class="param-checkbox mt-0 pt-0"/>
                    </td>
                    <td data-label="Min">
                      <MankTextField
                        v-model.number="row.min"
                        dense
                        hide-details
                        type="number"
                        placeholder="—"
                        :disabled="!paramTypeAllowsNumericBounds(row.type)"
                        class="param-field"/>
                    </td>
                    <td data-label="Max">
                      <MankTextField
                        v-model.number="row.max"
                        dense
                        hide-details
                        type="number"
                        min="0"
                        step="any"
                        :placeholder="paramMaxPlaceholder(row.type)"
                        :disabled="!paramTypeAllowsMaxField(row.type)"
                        class="param-field"/>
                    </td>
                    <td data-label="Default">
                      <MankTextField
                        v-model="row.default_value"
                        dense
                        hide-details
                        placeholder="default"
                        class="param-field"
                        :disabled="!paramTypeAllowsDefault(row.type)"/>
                    </td>
                    <td class="td-action" data-label="">
                      <v-btn
                        icon
                        small
                        class="param-remove"
                        @click="removeParam(idx)">
                        <v-icon size="20" color="icon-muted">
                          mdi-close
                        </v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <v-btn
              text
              color="primary"
              class="add-param-btn mt-2 px-0"
              @click="addParam">
              <v-icon left small>
                mdi-plus
              </v-icon>
              Add parameter
            </v-btn>
          </div>
        </div>
      </div>

      <div
        v-if="!loadingDetail"
        class="footer-buttons">
        <v-btn
          variant="text"
          size="medium"
          class="mank-btn"
          elevation="0"
          @click="cancel">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>

        <v-btn
          v-if="!loadError"
          variant="flat"
          color="primary"
          size="medium"
          class="mank-btn"
          elevation="0"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit">
          {{ isEdit ? 'Save changes' : 'Create' }}
        </v-btn>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import AgentService from '@/services/AgentService';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';

const emptyParam = () => ({
  name: '',
  type: 'string',
  required: false,
  min: '',
  max: '',
  default_value: '',
});

const normalizeParamTypeKey = (type) => String(type ?? 'string').trim().toLowerCase();

export default {
  name: 'AgentModelForm',
  components: { Icon, DeleteConfirmationModal },
  props: {
    id: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loadingDetail: false,
      loadError: '',
      saving: false,
      deleting: false,
      showDeleteDialog: false,
      form: {
        name: '',
        description: '',
        source: 'TTS',
        external_path: '',
        content_type: '',
        params: [],
      },
      paramTypes: [
        { text: 'string', value: 'string' },
        { text: 'text', value: 'text' },
        { text: 'int', value: 'int' },
        { text: 'float', value: 'float' },
        { text: 'bool', value: 'bool' },
        { text: 'file', value: 'file' },
      ],
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.id);
    },
    sourceSelectItems() {
      const s = (this.form.source || '').trim() || 'TTS';
      if (s !== 'TTS') {
        return [s];
      }
      return ['TTS'];
    },
    canSubmit() {
      const pathOk = (this.form.external_path || '').trim().length > 0;
      return this.form.name.trim().length > 0 && !!this.form.source && pathOk;
    },
  },
  mounted() {
    if (this.isEdit) {
      this.loadDetail();
    } else {
      this.form.params = [emptyParam()];
    }
  },
  methods: {
    cancelDelete() {
      this.showDeleteDialog = false;
    },
    async confirmDelete() {
      if (!this.id) {
        this.showDeleteDialog = false;
        return;
      }
      this.deleting = true;
      try {
        const res = await AgentService.deleteAgentModel(this.id);
        if (res?.success !== false) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            message: 'Agent Model deleted.',
          }, { root: true });
          await this.$router.push({ name: 'AgentModels' });
        } else {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: res?.message || 'Could not delete Agent Model.',
          }, { root: true });
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Could not delete Agent Model.',
        }, { root: true });
      } finally {
        this.deleting = false;
        this.showDeleteDialog = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'AgentModels' });
    },
    coalesceDefault(p) {
      if (p.default_value != null) return String(p.default_value);
      if (p.DefaultValue != null) return String(p.DefaultValue);
      return '';
    },
    addParam() {
      this.form.params.push(emptyParam());
    },
    removeParam(i) {
      this.form.params.splice(i, 1);
      if (this.form.params.length === 0) {
        this.form.params.push(emptyParam());
      }
    },
    paramTypeAllowsNumericBounds(type) {
      const k = normalizeParamTypeKey(type);
      return k === 'int' || k === 'float';
    },
    paramTypeAllowsFileMaxMb(type) {
      const k = normalizeParamTypeKey(type);
      return k === 'file' || k === 'formfile' || k === 'form_file';
    },
    paramTypeAllowsMaxField(type) {
      return this.paramTypeAllowsNumericBounds(type) || this.paramTypeAllowsFileMaxMb(type);
    },
    paramMaxPlaceholder(type) {
      if (this.paramTypeAllowsFileMaxMb(type)) return 'MB';
      return '—';
    },
    paramTypeAllowsDefault(type) {
      const k = normalizeParamTypeKey(type);
      return k !== 'file' && k !== 'formfile' && k !== 'form_file';
    },
    onParamTypeChange(row) {
      if (!this.paramTypeAllowsNumericBounds(row.type)) {
        row.min = '';
      }
      if (!this.paramTypeAllowsMaxField(row.type)) {
        row.max = '';
      }
      if (!this.paramTypeAllowsDefault(row.type)) {
        row.default_value = '';
      }
    },
    normalizeParam(p) {
      const typeRaw = String(p.type ?? p.Type ?? 'string');
      const k = normalizeParamTypeKey(typeRaw);
      let type = k === 'boolean' ? 'bool' : k;
      if (type === 'formfile' || type === 'form_file') type = 'file';
      const allowsBounds = this.paramTypeAllowsNumericBounds(type);
      const allowsFileMax = this.paramTypeAllowsFileMaxMb(type);
      const allowsDef = this.paramTypeAllowsDefault(type);
      const maxRaw = p.max ?? p.Max ?? '';
      return {
        name: String(p.name ?? p.Name ?? ''),
        type,
        required: !!(p.required ?? p.Required),
        min: allowsBounds ? (p.min ?? p.Min ?? '') : '',
        max: (allowsBounds || allowsFileMax) ? maxRaw : '',
        default_value: allowsDef ? this.coalesceDefault(p) : '',
      };
    },
    applyModel(raw) {
      const paramsRaw = raw.params ?? raw.Params ?? [];
      const ct = raw.content_type ?? raw.ContentType ?? raw.contentType ?? '';
      this.form = {
        name: String(raw.name ?? raw.Name ?? ''),
        description: String(raw.description ?? raw.Description ?? ''),
        source: String(raw.source ?? raw.Source ?? 'TTS'),
        external_path: String(
          raw.external_path ?? raw.ExternalPath ?? raw.endpoint_url ?? raw.EndpointUrl ?? '',
        ),
        content_type: ct != null ? String(ct) : '',
        params: Array.isArray(paramsRaw) && paramsRaw.length
          ? paramsRaw.map((p) => this.normalizeParam(p))
          : [emptyParam()],
      };
    },
    async loadDetail() {
      this.loadingDetail = true;
      this.loadError = '';
      try {
        const res = await AgentService.getAgentModelById(this.id);
        if (res?.success && res.data) {
          this.applyModel(res.data);
        } else {
          this.loadError = 'Agent Model not found or access denied.';
        }
      } catch (e) {
        this.loadError = e?.message || 'Failed to load Agent Model.';
      } finally {
        this.loadingDetail = false;
      }
    },
    buildPayload() {
      const params = this.form.params
        .filter((p) => p.name.trim() && p.type)
        .map((p) => {
          const o = {
            name: p.name.trim(),
            type: p.type,
            required: !!p.required,
          };
          if (
            this.paramTypeAllowsDefault(p.type)
            && p.default_value !== ''
            && p.default_value != null
          ) {
            o.default_value = String(p.default_value);
          }
          if (this.paramTypeAllowsNumericBounds(p.type)) {
            if (p.min !== '' && p.min != null && !Number.isNaN(Number(p.min))) {
              o.min = Number(p.min);
            }
            if (p.max !== '' && p.max != null && !Number.isNaN(Number(p.max))) {
              o.max = Number(p.max);
            }
          } else if (this.paramTypeAllowsFileMaxMb(p.type)) {
            if (p.max !== '' && p.max != null && !Number.isNaN(Number(p.max)) && Number(p.max) > 0) {
              o.max = Number(p.max);
            }
          }
          return o;
        });
      const body = {
        name: this.form.name.trim(),
        source: this.form.source,
        description: (this.form.description || '').trim(),
        external_path: (this.form.external_path || '').trim(),
        content_type: (this.form.content_type || '').trim(),
        params,
      };
      return body;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      try {
        const body = this.buildPayload();
        let res;
        if (this.isEdit) {
          res = await AgentService.updateAgentModel(this.id, body);
        } else {
          res = await AgentService.createAgentModel(body);
        }
        if (res?.success !== false) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            message: this.isEdit ? 'Agent Model updated.' : 'Agent Model created.',
          }, { root: true });
          await this.$router.push({ name: 'AgentModels' });
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Save failed.',
        }, { root: true });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.page-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
}

.header-right {
  display: flex;
  align-items: center;
}

.main-content {
  padding: 24px;
  overflow-y: auto;
}

/* TTS.vue ile aynı oran: dar sol (420px), geniş sağ (flex: 1) */
.main-content.main-content--split-row {
  align-items: flex-start;
  gap: 24px;
  overflow: hidden;
}

.agent-model-split-inner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
}

.left-panel {
  flex: 0 0 420px;
  width: 100%;
  max-width: 420px;
  min-width: 0;
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;
  flex-shrink: 0;

  .mank-btn {
    height: 32px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
}

.section-title--spaced {
  margin-top: 32px;
}

/* Agent edit (AgentPage) ile aynı description / system prompt görünümü */
.description-field {
  margin-bottom: 20px;
}

.system-prompt-section {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.text-box {
  position: relative;
  height: 100%;

  .system-prompt-textarea {
    height: 100%;

    ::v-deep .v-input__slot {
      align-items: stretch;
      min-height: 120px;
      background: $light-surface-1 !important;
      border-radius: $card-border-radius;
      border: none;
      box-shadow: none;
    }

    ::v-deep fieldset {
      border: none;
    }

    ::v-deep textarea {
      min-height: 120px;

      &::placeholder {
        color: $light-foreground-3;
      }
    }
  }
}

.form-group {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  font-size: $text-size-13;
  font-weight: 600;
  margin-bottom: 8px;
  color: $light-foreground-1;

  &.required .required-asterisk {
    color: $light-primary;
    font-weight: 700;
  }
}

.info-line {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.info-text {
  font-size: $text-size-13;
  color: $light-foreground-3;
  line-height: 1.5;
}

.error-banner {
  background: rgba($error-500, 0.08);
  border: 1px solid rgba($error-500, 0.28);
  border-radius: 8px;
  color: $error-700;
}

.params-help {
  color: $light-foreground-3 !important;
}

.params-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.params-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  table-layout: fixed;

  colgroup .colgroup-name {
    width: 22%;
  }

  colgroup .colgroup-type {
    width: 16%;
  }

  colgroup .colgroup-req {
    width: 10%;
  }

  colgroup .colgroup-num {
    width: 11%;
  }

  colgroup .colgroup-def {
    width: 24%;
  }

  colgroup .colgroup-act {
    width: 6%;
  }

  thead th {
    text-align: left;
    font-size: $text-size-11;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $light-foreground-4;
    padding: 0 10px 10px 0;
    vertical-align: bottom;
    border-bottom: 1px solid $light-border;

    .th-sub {
      display: block;
      margin-top: 2px;
      font-size: 10px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0.02em;
      color: $light-foreground-4;
      opacity: 0.85;
    }
  }

  thead th.th-action {
    width: 44px;
    padding-right: 0;
  }

  tbody tr {
    border-bottom: 1px solid $light-border;

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    vertical-align: middle;
    padding: 10px 10px 10px 0;
  }

  tbody td.td-action {
    width: 44px;
    text-align: center;
    padding-right: 0;
    vertical-align: middle;
  }

  &::v-deep .param-field.v-input {
    margin-top: 0;
    font-size: $body-3-size;
  }

  &::v-deep .param-checkbox.v-input {
    margin-top: 0;
    padding-top: 0;
  }

  &::v-deep .param-checkbox .v-input--selection-controls__input {
    margin-right: 0;
  }
}

.param-remove::v-deep .v-btn {
  color: $light-foreground-4;
}

.add-param-btn::v-deep .v-btn {
  color: $light-primary !important;
  font-weight: 600;
}

@media (max-width: 959px) {
  .agent-model-split-inner {
    flex-direction: column;
  }

  .left-panel,
  .right-panel {
    flex: 1 1 auto;
    width: 100%;
    max-width: none;
  }

  .params-table {
    min-width: 0;
    display: block;

    colgroup {
      display: none;
    }

    thead {
      display: none;
    }

    tbody {
      display: block;
    }

    tbody tr {
      display: block;
      border: 1px solid $light-border;
      border-radius: 8px;
      margin-bottom: 12px;
      padding: 8px 12px 4px;
    }

    tbody td {
      display: block;
      padding: 10px 0;
      border: none;
    }

    tbody td[data-label]:not([data-label=''])::before {
      content: attr(data-label);
      display: block;
      font-size: $text-size-11;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: $light-foreground-4;
      margin-bottom: 6px;
    }

    tbody td.td-action {
      text-align: right;
      padding-top: 4px;
    }

    tbody td.td-action::before {
      display: none;
    }
  }
}
</style>
